import type { ComponentType, SVGProps } from 'react';
import { cn } from '../../lib/utils';
import {
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon,
  CursorArrowRaysIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

export type WorkflowTool = 'select' | 'pan' | 'comment' | 'download';

interface WorkflowCanvasToolsProps {
  active: WorkflowTool;
  onChange: (tool: WorkflowTool) => void;
  className?: string;
}

const tools: { id: WorkflowTool; icon: IconComponent; label: string; shortcut: string }[] = [
  { id: 'select', icon: CursorArrowRaysIcon, label: 'Select', shortcut: 'V' },
  { id: 'pan', icon: HandRaisedIcon, label: 'Pan', shortcut: 'H' },
  { id: 'comment', icon: ChatBubbleLeftEllipsisIcon, label: 'Comment', shortcut: 'C' },
  { id: 'download', icon: ArrowDownTrayIcon, label: 'Export canvas', shortcut: '⌘E' },
];

export default function WorkflowCanvasTools({
  active,
  onChange,
  className,
}: WorkflowCanvasToolsProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-xl border border-border bg-background/95 p-1 shadow-sm backdrop-blur',
        className,
      )}
      role="toolbar"
      aria-label="Canvas tools"
    >
      {tools.map((t) => {
        const ToolIcon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            aria-label={`${t.label} (${t.shortcut})`}
            aria-pressed={active === t.id}
            title={`${t.label} · ${t.shortcut}`}
            className={cn(
              'group relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              active === t.id
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <ToolIcon className="h-5 w-5" />
            {/* Shortcut chip on the right side of the toolbar — visible on hover */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-foreground px-1.5 py-1 text-[10px] font-medium tracking-wide text-background opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
            >
              {t.label} · <span className="font-mono">{t.shortcut}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
